import { _isNotEmpty, _notNil } from '@/littledash';
import type { SendIncompleteReason, SendRuleIdentity } from '@/model/Agent.model';
import type { FC } from 'react';
import styles from './SendResults.module.scss';
import SendRuleId from './SendRuleId';
import { plural } from '@/components/Agents/SendValidator/utils/SendText';
import { REASON_TEXT } from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';

/** One notice that a check could not decide something, with where it applied. */
export interface SendDisclosure {
  key: string;
  reason: SendIncompleteReason;
  message: string;
  rule?: SendRuleIdentity;
  /** Where it applied: a column and row, or a domain set. */
  where?: string;
}

interface SendDisclosureListProps {
  disclosures: ReadonlyArray<SendDisclosure>;
  /**
   * How many checks could not decide something, as the server counted them. The notices listed can
   * differ: a limit can leave one out, and one check can emit several.
   */
  undecidedChecks: number;
  testId: string;
}

/**
 * The checks that could not decide something, set apart from what the checks found and drawn
 * neutrally, without a severity: a notice like this is not a finding about the data, so it is never
 * filtered by severity and never counted among the findings. Renders nothing when there is none.
 */
const SendDisclosureList: FC<SendDisclosureListProps> = ({ disclosures, undecidedChecks, testId }) => {
  if (undecidedChecks === 0 && !_isNotEmpty(disclosures)) {
    return null;
  }
  return (
    <div className={styles.disclosures} data-test-component="SendDisclosureList" data-testid={testId}>
      <h4 className={styles.disclosuresTitle} data-test-element="disclosures-count">
        {plural(undecidedChecks, "check couldn't decide everything", "checks couldn't decide everything")}
      </h4>
      <p className={styles.disclosuresNote}>
        These are not findings about the data: the checks below could not complete, so what they would have checked is
        unknown.
      </p>
      {_isNotEmpty(disclosures) && (
        <ul className={styles.disclosureItems}>
          {disclosures.map((disclosure) => (
            <li
              key={disclosure.key}
              className={styles.disclosureItem}
              data-test-element="disclosure"
              data-test-key={disclosure.reason}
            >
              <div className="f6 fw6 near-black">
                {_notNil(disclosure.rule) && (
                  <SendRuleId id={disclosure.rule.id} publisher={disclosure.rule.publisher} className="mr2" />
                )}
                {REASON_TEXT[disclosure.reason] ?? disclosure.reason}
                {_notNil(disclosure.where) ? ` · ${disclosure.where}` : ''}
              </div>
              <p className="f6 dark-gray ma0 mt1">{disclosure.message}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SendDisclosureList;
